import { CanvasEditor } from './CanvasEditor';
import * as THREE from 'three';
import { t } from '../i18n/translations';

export class EditorUI {
  private editor?: CanvasEditor;
  private modal: HTMLElement;
  private isOpen: boolean = false;
  private onApplyCallback?: (dataUrl: string, canvasData?: any, uvGroupIds?: string[]) => void;
  private onRealtimeCallback?: (dataUrl: string) => void;
  private onCancelCallback?: () => void;
  private onUVGroupFilterChange?: (uvGroupIds: string[]) => void;
  private isEditingExisting: boolean = false;
  private currentEditingPrintId: string | null = null;
  private initialCanvasState: string | null = null;

  private editorInitialized: boolean = false;
  private originalCanvasWidth: string = '100vw';
  private originalCameraState: { position: THREE.Vector3; target: THREE.Vector3 } | null = null;

  // UV group switcher
  private activeUVGroupIds: Set<string> = new Set();

  // Context menu for mask actions (right click in print editor)
  private maskContextMenu: HTMLElement | null = null;
  private maskContextMenuMaskItem: HTMLElement | null = null;
  private maskContextMenuDivider: HTMLElement | null = null;

  constructor() {
    this.modal = this.createModal();
    // Canvas будет создан после того, как modal добавлен в DOM
    this.setupEventListeners();
  }
  
  private initializeEditor(): void {
    if (this.editorInitialized) return;
    
    const canvasElement = document.getElementById('print-canvas') as HTMLCanvasElement;
    if (canvasElement && !this.editor) {
      // Инициализируем "рабочий" размер канваса под видимый контейнер,
      // иначе Fabric будет рендерить на 4096x4096 каждый кадр (очень тяжело).
      const uvContainer = document.getElementById('uv-areas-container');
      const rect = uvContainer?.getBoundingClientRect();
      const vw = rect?.width && rect.width > 0 ? Math.round(rect.width) : 512;
      const vh = rect?.height && rect.height > 0 ? Math.round(rect.height) : 512;

      this.editor = new CanvasEditor('print-canvas', {
        width: vw,
        height: vh,
        backgroundColor: 'transparent' // Прозрачный фон!
      });
      
      // Устанавливаем callback для обновления кнопки зума при зуме мышью
      this.editor.setOnZoomCallback(() => {
        this.updateZoomLabel();
      });

      // Mask tool UI syncing
      this.editor.setSelectionChangeCallback(() => {
        this.updateMaskControls();
      });
      this.editor.setMaskStateChangeCallback((active) => {
        this.updateMaskControls(active);
      });
      this.updateMaskControls();
      
      this.editorInitialized = true;
    }
  }
  
  private updateZoomLabel(): void {
    const btn = document.getElementById('print-zoom-reset');
    if (!btn || !this.editor) return;
    const pct = Math.round(this.editor.getZoomFactor() * 100);
    btn.textContent = `${pct}%`;
  }

  private updateMaskControls(forceMaskActive?: boolean): void {
    const hint = document.getElementById('mask-hint') as HTMLElement | null;
    if (!hint) return;

    const editor = this.editor;
    const isActive = forceMaskActive !== undefined ? forceMaskActive : Boolean(editor?.isMaskCreationActive());

    hint.classList.toggle('hidden', !isActive);
  }

  private createModal(): HTMLElement {
    const modal = document.createElement('div');
    modal.id = 'canvas-editor-modal';
    modal.style.cssText = `
      position: fixed;
      top: 50%;
      right: 0px;
      transform: translateY(-50%);
      width: 50vw;
      height: 100vh;
      background-color: rgb(16, 16, 16);
      background-image: linear-gradient(45deg, rgb(24, 24, 24) 25%, transparent 25%), linear-gradient(-45deg, rgb(24, 24, 24) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgb(24, 24, 24) 75%), linear-gradient(-45deg, transparent 75%, rgb(24, 24, 24) 75%);
      background-size: 20px 20px;
      background-position: 0px 0px, 0px 10px, 10px -10px, -10px 0px;
      z-index: 10000;
      display: none;
      overflow-y: auto;
    `;

    modal.innerHTML = `
      <div class="editor-container">
        <!-- Заголовок с кнопкой Close -->
        <div class="editor-header">
          <button id="add-image" class="editor-add-image-btn" data-i18n="editor.add.image">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.1 6a5 5 0 0 0-.09 1.3H6.65A1.35 1.35 0 0 0 5.3 8.65v9c0 .746.605 1.35 1.35 1.35h9A1.35 1.35 0 0 0 17 17.65V11.9a5 5 0 0 0 1.3.09v5.66a2.65 2.65 0 0 1-2.65 2.65h-9A2.65 2.65 0 0 1 4 17.65v-9A2.65 2.65 0 0 1 6.65 6z" fill="#fff"/>
              <path d="M8.136 12.223a.953.953 0 0 1 1.734-.029l.976 1.915c.295.58 1.02.724 1.494.296l.781-.706a.94.94 0 0 1 1.376.114l.914 1.13c.543.67.093 1.707-.741 1.707H7.627c-.726 0-1.199-.81-.874-1.498zm5.146-3.566c.267.76.712 1.434 1.285 1.976a1.5 1.5 0 1 1-1.285-1.976M18 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8m-.001 1.4a.61.61 0 0 0-.607.608v1.386h-1.386a.61.61 0 0 0-.608.607l.013.122a.61.61 0 0 0 .595.485h1.386v1.386c0 .335.272.608.607.608l.122-.013a.61.61 0 0 0 .485-.595V7.608h1.386a.61.61 0 0 0 .608-.607l-.013-.122a.61.61 0 0 0-.595-.485h-1.386V5.008l-.012-.122a.61.61 0 0 0-.473-.473zm2.33 2.372q.068.1.07.229a.41.41 0 0 1-.407.407h-1.586v1.586-1.587h1.585a.408.408 0 0 0 .375-.565zm-4.597.53a.4.4 0 0 0 .273.105l1.586.001h-1.585a.4.4 0 0 1-.228-.07zm2.604-2.523q.068.1.07.229v1.585-1.586a.4.4 0 0 0-.033-.158z" fill="#fff"/>
            </svg>
            ${t('editor.add.image')}
          </button>
          <input type="file" id="image-upload" accept="image/*" multiple class="editor-file-input">
          <button id="close-editor" class="editor-close-btn" data-i18n="editor.close">${t('editor.close')}</button>
        </div>
        
        <!-- Основная область с UV развертками -->
        <div class="editor-main-area">
          <!-- Область UV развертки с прозрачным фоном -->
          <div id="uv-areas-container" class="editor-uv-container">
            <!-- Canvas для редактирования поверх UV развертки -->
            <div id="canvas-wrapper" class="editor-canvas-wrapper">
              <canvas id="print-canvas" class="editor-canvas"></canvas>
              <div id="mask-hint" class="editor-mask-hint hidden">${t('editor.mask.hint')}</div>
            </div>
          </div>
          
          <!-- Панель управления внизу (только Лекала) -->
          <div class="editor-controls-panel">
            <div id="uv-group-switcher" class="editor-uv-group-switcher hidden">
              <button type="button" class="editor-uv-group-trigger" aria-expanded="false" aria-haspopup="true">Лекала</button>
              <div class="editor-uv-group-panel" role="listbox" aria-multiselectable="true"></div>
            </div>
          </div>

          <!-- Zoom controls — всегда справа снизу -->
          <div class="editor-zoom-controls">
            <button id="print-zoom-out" class="editor-zoom-btn" title="Zoom out">−</button>
            <button id="print-zoom-reset" class="editor-zoom-btn editor-zoom-btn-wide" title="Reset zoom">100%</button>
            <button id="print-zoom-in" class="editor-zoom-btn" title="Zoom in">+</button>
          </div>
        </div>
        
      <!-- Скрытые элементы для функций, которые нужны, но не отображаются в основном интерфейсе -->
      <div class="editor-hidden">
        <button id="add-text">📝 Добавить текст</button>
        <button id="delete-selected">🗑️ Удалить</button>
        <button id="clear-canvas">🗑️ Очистить все</button>
        <button id="apply-editor">✓ Применить</button>
      </div>
      
      <style>
        .editor-container {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-shrink: 0;
          padding: 20px;
        }
        
        .editor-main-area {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 20px;
          overflow: hidden;
          min-height: 0;
          position: relative;
        }
        
        .editor-uv-container {
          background: transparent;
          flex: 1;
          min-height: 0;
          overflow: hidden;
          position: relative;
          border-radius: 8px;
        }
        
        .editor-canvas-wrapper {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: all;
          z-index: 10;
          overflow: hidden;
        }
        
        .editor-canvas {
          width: 100%;
          height: 100%;
          display: block;
        }
        
        .editor-controls-panel {
          flex-shrink: 0;
          display: flex;
          flex-direction: row;
          gap: 12px;
          align-items: center;
          padding: 20px;
        }

        .editor-mask-hint {
          position: absolute;
          left: 12px;
          top: 12px;
          z-index: 20;
          padding: 8px 10px;
          border-radius: 10px;
          background: rgba(0, 0, 0, 0.55);
          color: rgba(255,255,255,0.92);
          border: 1px solid rgba(255,255,255,0.18);
          font-size: 12px;
          line-height: 1.2;
          max-width: 70%;
          pointer-events: none;
          backdrop-filter: blur(6px);
        }
        
        .editor-file-input {
          display: none;
        }
        
        .editor-zoom-controls {
          position: absolute;
          bottom: 20px;
          right: 20px;
          display: flex;
          gap: 6px;
          align-items: center;
          flex-shrink: 0;
          z-index: 15;
        }
        
        .editor-hidden {
          display: none;
        }
        
        .editor-btn {
          width: 100%;
          padding: 10px;
          border: 0;
          border-radius: 8px;
          background: linear-gradient(135deg, #4cc9f0, #4361ee);
          color: #fff;
          cursor: pointer;
          font-weight: 600;
          font-size: 13px;
          text-align: left;
        }
        
        .editor-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(76, 201, 240, .3);
        }
        
        .editor-btn-small {
          width: 100%;
          padding: 8px;
          border: 0;
          border-radius: 6px;
          background: #263050;
          color: #dfe7ff;
          cursor: pointer;
          font-size: 12px;
        }
        
        .editor-btn-small:hover {
          background: #2a3b5f;
        }
        
        .editor-btn-danger {
          width: 100%;
          padding: 8px;
          border: 0;
          border-radius: 6px;
          background: linear-gradient(135deg, #f72585, #b5179e);
          color: #fff;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
        }
        
        .editor-btn-danger:hover {
          transform: translateY(-1px);
        }
        
        .editor-btn-cancel {
          padding: 12px 24px;
          border: 0;
          border-radius: 10px;
          background: #263050;
          color: #dfe7ff;
          cursor: pointer;
          font-weight: 600;
        }
        
        .editor-btn-cancel:hover {
          background: #2a3b5f;
        }
        
        .editor-btn-apply {
          padding: 12px 24px;
          border: 0;
          border-radius: 10px;
          background: linear-gradient(135deg, #4cc9f0, #4361ee);
          color: #fff;
          cursor: pointer;
          font-weight: 600;
        }
        
        .editor-btn-apply:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(76, 201, 240, .35);
        }
        
        .editor-close-btn {
          position: relative;
          min-width: 71px;
          height: 30px;
          border-radius: 8px;
          background: #292929;
          border: none;
          color: #999;
          font-size: 14px;
          font-weight: 400;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 16px;
        }
        
        .editor-close-btn:hover {
          background: #333;
          color: #fff;
        }
        
        .editor-add-image-btn {
          position: relative;
          min-width: 71px;
          height: 30px;
          border-radius: 8px;
          background: #292929;
          border: none;
          color: #999;
          font-size: 14px;
          font-weight: 400;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 16px;
          gap: 8px;
        }
        
        .editor-add-image-btn svg {
          flex-shrink: 0;
        }
        
        .editor-add-image-btn:hover {
          background: #333;
          color: #fff;
        }

        .editor-zoom-btn {
          min-width: 32px;
          width: 32px;
          height: 30px;
          border-radius: 8px;
          border: none;
          background: #292929;
          color: #999;
          cursor: pointer;
          font-size: 16px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          padding: 0;
          line-height: 1;
        }

        .editor-zoom-btn-wide {
          width: 56px;
          min-width: 56px;
          font-size: 12px;
          font-weight: 500;
        }

        .editor-zoom-btn:hover {
          background: #333;
          color: #fff;
        }

        .editor-uv-group-switcher {
          position: relative;
          flex-shrink: 0;
        }

        .editor-uv-group-switcher.hidden {
          display: none;
        }

        .editor-uv-group-trigger {
          height: 30px;
          padding: 0 14px;
          border-radius: 8px;
          border: none;
          background: #292929;
          color: #999;
          cursor: pointer;
          font-size: 14px;
          font-weight: 400;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .editor-uv-group-trigger:hover {
          background: #333;
          color: #fff;
        }

        .editor-uv-group-trigger::after {
          content: '';
          width: 0;
          height: 0;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-top: 5px solid currentColor;
          opacity: 0.8;
        }

        .editor-uv-group-switcher.open .editor-uv-group-trigger::after {
          transform: rotate(180deg);
        }

        .editor-uv-group-panel {
          display: none;
          position: absolute;
          left: 0;
          bottom: 100%;
          margin-bottom: 6px;
          min-width: 160px;
          max-height: 240px;
          overflow-y: auto;
          background: #1a1a1a;
          border-radius: 10px;
          z-index: 100;
          padding: 6px 0;
        }

        .editor-uv-group-switcher.open .editor-uv-group-panel {
          display: block;
        }

        .editor-uv-group-item.active {
          color: #fff;
        }

        .editor-uv-group-item {
          display: flex;
          align-items: center;
          gap: 9px;
          width: 100%;
          padding: 5px 12px;
          border: none;
          border-radius: 0;
          background: transparent;
          color: #ccc;
          cursor: pointer;
          font-size: 13px;
          text-align: left;
          transition: background 0.15s;
        }

        .editor-uv-group-item.active .editor-uv-group-item-check::after {
          content: '✓';
          font-size: 11px;
          color: #000000;
        }

        .editor-uv-group-item.active .editor-uv-group-item-check {
          background: #D9F903;
          border-color: #000000;
        }

        .editor-uv-group-item-check {
          width: 13px;
          height: 13px;
          flex-shrink: 0;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
      </style>
    `;

    document.body.appendChild(modal);

    // Context menu element (outside modal so it can overflow)
    if (!this.maskContextMenu) {
      const menu = document.createElement('div');
      menu.id = 'print-mask-context-menu';
      menu.className = 'fabrica-file-menu fabrica-print-mask-menu hidden';
      menu.innerHTML = `
        <div class="fabrica-dropdown-item" data-action="create-mask">
          <span>${t('editor.mask.create')}</span>
        </div>
      `;
      document.body.appendChild(menu);
      this.maskContextMenu = menu;
      this.maskContextMenuMaskItem = menu.querySelector('.fabrica-dropdown-item[data-action="create-mask"]') as HTMLElement | null;
      this.maskContextMenuDivider = menu.querySelector('[data-role="mask-divider"]') as HTMLElement | null;
    }

    return modal;
  }

  private hideMaskContextMenu(): void {
    this.maskContextMenu?.classList.add('hidden');
  }

  private showMaskContextMenu(clientX: number, clientY: number): void {
    if (!this.editor || !this.maskContextMenu || !this.maskContextMenuMaskItem) return;
    if (this.editor.isMaskCreationActive()) return;

    // Show action if we can create a mask on a single object OR remove an existing mask (including multi-select)
    const hasMask = this.editor.selectedHasMask();
    const canCreate = this.editor.canStartMaskCreation();
    if (!hasMask && !canCreate) return;

    const labelSpan = this.maskContextMenuMaskItem.querySelector('span');
    if (labelSpan) labelSpan.textContent = hasMask ? t('editor.mask.remove') : t('editor.mask.create');
    this.maskContextMenuMaskItem.setAttribute('data-action', hasMask ? 'remove-mask' : 'create-mask');
    this.maskContextMenuMaskItem.classList.remove('hidden');
    this.maskContextMenuDivider?.classList.add('hidden');

    this.maskContextMenu.classList.remove('hidden');
    this.maskContextMenu.style.left = `${Math.round(clientX)}px`;
    this.maskContextMenu.style.top = `${Math.round(clientY)}px`;

    requestAnimationFrame(() => {
      if (!this.maskContextMenu) return;
      const r = this.maskContextMenu.getBoundingClientRect();
      const pad = 8;
      let x = clientX;
      let y = clientY;
      if (x + r.width > window.innerWidth - pad) x = window.innerWidth - r.width - pad;
      if (y + r.height > window.innerHeight - pad) y = window.innerHeight - r.height - pad;
      if (x < pad) x = pad;
      if (y < pad) y = pad;
      this.maskContextMenu.style.left = `${Math.round(x)}px`;
      this.maskContextMenu.style.top = `${Math.round(y)}px`;
    });
  }

  private setupEventListeners(): void {
    // Закрытие модалки
    document.getElementById('close-editor')?.addEventListener('click', () => this.close());

    // Zoom controls (могут быть нажаты до initializeEditor)
    document.getElementById('print-zoom-in')?.addEventListener('click', () => {
      if (!this.editor) return;
      this.editor.zoomIn();
      this.updateZoomLabel();
    });
    document.getElementById('print-zoom-out')?.addEventListener('click', () => {
      if (!this.editor) return;
      this.editor.zoomOut();
      this.updateZoomLabel();
    });
    document.getElementById('print-zoom-reset')?.addEventListener('click', () => {
      if (!this.editor) return;
      this.editor.resetZoom();
      // updateZoomLabel вызывается через callback в resetZoom
    });
    
    // Применить
    document.getElementById('apply-editor')?.addEventListener('click', () => {
      if (this.editor) {
        // ВАЖНО: Сохраняем и dataURL (для текстуры) и canvasData (для структуры объектов)
        const dataUrl = this.editor.exportToDataURL('png', 1.0);
        const canvasData = this.editor.saveCanvasData();
        if (this.onApplyCallback) {
          this.onApplyCallback(dataUrl, canvasData, Array.from(this.activeUVGroupIds));
        }
        // Обновляем initialCanvasState, чтобы при следующем закрытии не было ложных предупреждений
        this.initialCanvasState = JSON.stringify(canvasData);
        this.close();
      }
    });

    // Инструменты
    document.getElementById('add-text')?.addEventListener('click', () => {
      if (this.editor) {
        this.editor.addText();
      }
    });
    
    document.getElementById('add-image')?.addEventListener('click', () => {
      document.getElementById('image-upload')?.click();
    });
    
    document.getElementById('image-upload')?.addEventListener('change', (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files || files.length === 0 || !this.editor) return;
      
      // Обрабатываем все выбранные файлы (включая файлы с одинаковыми именами)
      Array.from(files).forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (this.editor) {
            const dataUrl = reader.result as string;
            this.editor.addImage(dataUrl).catch(() => alert('Не удалось добавить изображение'));
          }
        };
        reader.readAsDataURL(file);
      });
      
      // Сбрасываем input чтобы можно было повторно выбрать те же файлы
      (e.target as HTMLInputElement).value = '';
    });

    // Mask tool
    document.getElementById('mask-create-btn')?.addEventListener('click', () => {
      if (!this.editor) return;
      if (this.editor.isMaskCreationActive()) {
        this.editor.cancelMaskCreation();
        this.updateMaskControls(false);
        return;
      }
      const ok = this.editor.startMaskCreation();
      if (!ok) {
        alert(t('editor.mask.select.object'));
      }
      this.updateMaskControls(Boolean(ok));
    });
    document.getElementById('mask-remove-btn')?.addEventListener('click', () => {
      if (!this.editor) return;
      this.editor.removeMaskFromSelected();
      this.updateMaskControls();
    });

    // Right click context menu: Create/Remove mask for selected object
    const canvasWrapper = document.getElementById('canvas-wrapper') as HTMLElement | null;
    canvasWrapper?.addEventListener('contextmenu', (e: MouseEvent) => {
      if (!this.isOpen || !this.editor) return;
      if (this.editor.isMaskCreationActive()) return;
      // Show menu only if something is selected
      if (!this.editor.getCanvas()?.getActiveObject?.()) return;
      // Don't block default context menu if we have nothing actionable
      if (!this.editor.selectedHasMask() && !this.editor.canStartMaskCreation()) return;
      e.preventDefault();
      this.showMaskContextMenu(e.clientX, e.clientY);
    });

    this.maskContextMenu?.addEventListener('click', (e: MouseEvent) => {
      if (!this.editor) return;
      const target = e.target as HTMLElement | null;
      const item = target?.closest?.('.fabrica-dropdown-item') as HTMLElement | null;
      const action = item?.getAttribute('data-action');
      if (!action) return;
      e.preventDefault();
      e.stopPropagation();
      if (action === 'create-mask') {
        const ok = this.editor.startMaskCreation();
        if (!ok) alert(t('editor.mask.select.object'));
        this.updateMaskControls(Boolean(ok));
      } else if (action === 'remove-mask') {
        this.editor.removeMaskFromSelected();
        this.updateMaskControls();
      }
      this.hideMaskContextMenu();
    });

    document.addEventListener('mousedown', (e: MouseEvent) => {
      if (!this.maskContextMenu || this.maskContextMenu.classList.contains('hidden')) return;
      const target = e.target as HTMLElement | null;
      if (target && (target === this.maskContextMenu || target.closest('#print-mask-context-menu'))) return;
      this.hideMaskContextMenu();
    });

    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!this.maskContextMenu || this.maskContextMenu.classList.contains('hidden')) return;
      this.hideMaskContextMenu();
    });

    // Действия
    document.getElementById('delete-selected')?.addEventListener('click', () => {
      if (this.editor) {
        this.editor.deleteSelected();
      }
    });
    document.getElementById('clear-canvas')?.addEventListener('click', () => {
      if (confirm('Вы уверены, что хотите очистить холст?') && this.editor) {
        this.editor.clear();
      }
    });

  }

  public open(callback?: (dataUrl: string) => void): void {
    this.isOpen = true;
    this.onApplyCallback = callback;
    this.isEditingExisting = false;
    this.currentEditingPrintId = null;
    
    // Скрываем aspect-ratio overlay и bottom-toolbar при открытии редактора
    const aspectRatioOverlay = document.getElementById('aspect-ratio-overlay');
    const bottomToolbar = document.getElementById('bottom-toolbar');
    if (aspectRatioOverlay) {
      aspectRatioOverlay.classList.add('hidden');
    }
    if (bottomToolbar) {
      bottomToolbar.style.display = 'none';
    }
    
    this.modal.style.display = 'block';
    this.initializeEditor();
    // Сбрасываем зум при открытии
    if (this.editor) this.editor.resetZoom();
    if (this.editor) {
      this.editor.clear(); // Очищаем canvas перед открытием
      // Сохраняем начальное состояние (пустой канвас)
      this.initialCanvasState = JSON.stringify(this.editor.saveCanvasData());
    }
  }
  public openWithUVBackground(
    model: THREE.Group,
    callback?: (dataUrl: string, canvasData?: any, uvGroupIds?: string[]) => void,
    realtimeCallback?: (dataUrl: string) => void,
    isEditing?: boolean,
    printId?: string,
    cancelCallback?: () => void,
    uvGroupNames?: Record<string, string>,
    initialUVGroupIds?: string[] | null,
    uvGroupFilterChangeCallback?: (uvGroupIds: string[]) => void
  ): void {
    this.isOpen = true;
    this.onApplyCallback = callback;
    this.onRealtimeCallback = realtimeCallback;
    this.onCancelCallback = cancelCallback;
    this.onUVGroupFilterChange = uvGroupFilterChangeCallback;
    this.isEditingExisting = isEditing || false;
    this.currentEditingPrintId = printId || null;
    this.activeUVGroupIds = new Set((initialUVGroupIds || []).filter(Boolean)); // Выставляем группы принта (если есть)
    
    // Сохраняем исходное состояние сцены
    const canvasContainer = document.getElementById('canvas-container');
    if (canvasContainer) {
      const computedWidth = window.getComputedStyle(canvasContainer).width;
      this.originalCanvasWidth = computedWidth || '100vw';
      
      if (this.sceneManager) {
        const camera = (this.sceneManager as any).camera;
        const controls = (this.sceneManager as any).controls;
        if (camera && controls) {
          this.originalCameraState = {
            position: camera.position.clone(),
            target: controls.target.clone()
          };
        }
      }
      
      // Изменяем размер СРАЗУ
      canvasContainer.style.width = '50vw';
      canvasContainer.style.left = '0';
      
      // Перерисовываем сцену СРАЗУ
      if (this.sceneManager) {
        const renderer = (this.sceneManager as any).renderer;
        const camera = (this.sceneManager as any).camera;
        if (renderer) {
          const width = canvasContainer.offsetWidth;
          const height = canvasContainer.offsetHeight;
          renderer.setSize(width, height);
          if (camera) {
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
          }
        }
      }
    }
    
    // Скрываем aspect-ratio overlay и bottom-toolbar при открытии редактора
    const aspectRatioOverlay = document.getElementById('aspect-ratio-overlay');
    const bottomToolbar = document.getElementById('bottom-toolbar');
    if (aspectRatioOverlay) {
      aspectRatioOverlay.classList.add('hidden');
    }
    if (bottomToolbar) {
      bottomToolbar.style.display = 'none';
    }
    
    // ВАЖНО: Показываем modal ПЕРЕД инициализацией editor,
    // чтобы контейнер имел правильные размеры при первом открытии
    this.modal.style.display = 'block';
    
    // Устанавливаем курсор загрузки
    document.body.classList.add('loading-editor');
    
    // Показываем индикатор загрузки редактора
    const toastId = 'editor-loading';
    window.dispatchEvent(new CustomEvent('showLoadingToast', { 
      detail: { id: toastId, text: t('loading.editor'), progress: 0 } 
    }));
    
    // Ждём один кадр чтобы браузер отрендерил modal и контейнер получил размеры
    requestAnimationFrame(() => {
      // Прогресс 30% - modal отрендерен
      window.dispatchEvent(new CustomEvent('updateLoadingToast', { 
        detail: { id: toastId, progress: 30 } 
      }));
      
      // Инициализируем editor (теперь контейнер видим и имеет размеры)
      this.initializeEditor();
      
      // Прогресс 50% - editor инициализирован
      window.dispatchEvent(new CustomEvent('updateLoadingToast', { 
        detail: { id: toastId, progress: 50 } 
      }));
      
      // Настраиваем canvas
      this.setupUVAreas(model);
      
      // Прогресс 70% - UV настроен
      window.dispatchEvent(new CustomEvent('updateLoadingToast', { 
        detail: { id: toastId, progress: 70 } 
      }));
      
      // Очищаем и устанавливаем UV
        if (this.editor) {
          // Сбрасываем зум при открытии редактора
          this.editor.resetZoom();
          if (this.isEditingExisting) {
            this.editor.resetViewport();
            this.editor.setUVBackground(model, Array.from(this.activeUVGroupIds));
          } else {
            this.editor.resetViewport();
            this.editor.clear();
            this.editor.setUVBackground(model, Array.from(this.activeUVGroupIds));
            this.initialCanvasState = JSON.stringify(this.editor.saveCanvasData());
          }
          // Показываем переключатель UV-групп (если их больше одной)
          this.updateUVGroupSwitcher(model, uvGroupNames);
        }
      
      // Сбрасываем старые коллбэки от предыдущей сессии редактирования
      if (this.editor) {
        this.editor.setOnChangeCallback(undefined);
        this.editor.setOnRealtimeCallback(undefined);
      }
      // Реалтайм-обновление
      if (this.onRealtimeCallback && this.editor) {
        this.editor.setOnChangeCallback(this.onRealtimeCallback);
        this.editor.setOnRealtimeCallback(this.onRealtimeCallback);
      }
      
      // Прогресс 100% - готово
      window.dispatchEvent(new CustomEvent('updateLoadingToast', { 
        detail: { id: toastId, progress: 100 } 
      }));
      
      // Скрываем через небольшую задержку
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('hideLoadingToast', { 
          detail: { id: toastId } 
        }));
        // Убираем курсор загрузки
        document.body.classList.remove('loading-editor');
      }, 500);
    });
  }
  
  private sceneManager: any = null;
  
  public setSceneManager(sceneManager: any): void {
    this.sceneManager = sceneManager;
  }
  
  private setupUVAreas(model: THREE.Group): void {
    const uvContainer = document.getElementById('uv-areas-container');
    if (!uvContainer) return;
    
    const rect = uvContainer.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    
    const canvasWrapper = document.getElementById('canvas-wrapper');
    if (canvasWrapper) {
      canvasWrapper.style.width = rect.width + 'px';
      canvasWrapper.style.height = rect.height + 'px';
    }
    
    // ВАЖНО: НЕ меняем внутренний размер canvas (он должен оставаться 4096x4096)
    // Масштабирование происходит автоматически через CSS
    // Только обновляем размер wrapper для правильного отображения
    
    // Обновляем при изменении размера
    const resizeObserver = new ResizeObserver(() => {
      const newRect = uvContainer.getBoundingClientRect();
      if (canvasWrapper && newRect.width > 0 && newRect.height > 0) {
        canvasWrapper.style.width = newRect.width + 'px';
        canvasWrapper.style.height = newRect.height + 'px';
        // Обновляем рабочий размер Fabric canvas под viewport (для точного hit-test и FPS)
        if (this.editor) {
          this.editor.setViewportPixelSize(newRect.width, newRect.height);
          // При изменении размера пересчитываем масштаб и центрирование
          this.editor.syncUVBackgroundWithCanvas();
        }
      }
    });
    
    resizeObserver.observe(uvContainer);
  }
  public openWithImage(imageUrl: string, callback?: (dataUrl: string) => void): void {
    this.isOpen = true;
    this.onApplyCallback = callback;
    
    // Скрываем aspect-ratio overlay и bottom-toolbar при открытии редактора
    const aspectRatioOverlay = document.getElementById('aspect-ratio-overlay');
    const bottomToolbar = document.getElementById('bottom-toolbar');
    if (aspectRatioOverlay) {
      aspectRatioOverlay.classList.add('hidden');
    }
    if (bottomToolbar) {
      bottomToolbar.style.display = 'none';
    }
    
    this.modal.style.display = 'block';
    this.initializeEditor();
    
    if (!this.editor) return;
    
    this.editor.clear();
    
    // Загружаем изображение на canvas для редактирования
    this.editor.addImage(imageUrl).then(() => {
      // Изображение загружено, можно редактировать
      console.log('Print loaded for editing');
      // Сохраняем начальное состояние после загрузки изображения
      if (this.editor) {
        this.initialCanvasState = JSON.stringify(this.editor.saveCanvasData());
      }
    }).catch(err => {
      console.error('Failed to load print for editing:', err);
      alert('Не удалось загрузить принт для редактирования');
    });
    
    this.modal.style.display = 'block';
  }

  

  public openDesigner(callback?: (dataUrl: string, canvasData?: any) => void, realtimeCallback?: (dataUrl: string) => void): void {
    this.isOpen = true;
    this.onApplyCallback = callback;
    this.onRealtimeCallback = realtimeCallback;
    this.isEditingExisting = false;
    this.currentEditingPrintId = null;
    
    // Скрываем aspect-ratio overlay и bottom-toolbar при открытии редактора
    const aspectRatioOverlay = document.getElementById('aspect-ratio-overlay');
    const bottomToolbar = document.getElementById('bottom-toolbar');
    if (aspectRatioOverlay) {
      aspectRatioOverlay.classList.add('hidden');
    }
    if (bottomToolbar) {
      bottomToolbar.style.display = 'none';
    }
    
    this.modal.style.display = 'block';
    this.initializeEditor();
    
    if (!this.editor) return;
    
    // Чистый холст без UV
    this.editor.clear();
    // Сохраняем начальное состояние (пустой канвас)
    this.initialCanvasState = JSON.stringify(this.editor.saveCanvasData());
    if (this.onRealtimeCallback) {
      this.editor.setOnChangeCallback(this.onRealtimeCallback);
      this.editor.setOnRealtimeCallback(this.onRealtimeCallback);
    }
  }


  /**
   * Обновляет переключатель UV-групп на основе данных о группах модели.
   * Вызывается при открытии редактора с моделью.
   */
  private updateUVGroupSwitcher(
    model: THREE.Group,
    uvGroupNames?: Record<string, string> // groupId → отображаемое имя
  ): void {
    const switcher = document.getElementById('uv-group-switcher');
    if (!switcher || !this.editor) return;

    const groups = this.editor.getModelUVGroups(model);
    const groupIds = Object.keys(groups);

    // Скрываем если групп 0 или 1
    if (groupIds.length <= 1) {
      switcher.classList.add('hidden');
      this.activeUVGroupIds = new Set(groupIds[0] ? [groupIds[0]] : []);
      return;
    }

    switcher.classList.remove('hidden');

    const trigger = switcher.querySelector('.editor-uv-group-trigger') as HTMLButtonElement;
    const panel = switcher.querySelector('.editor-uv-group-panel') as HTMLElement;
    if (!trigger || !panel) return;

    // Если ничего не выбрано — выбираем ВСЕ группы (поведение как раньше)
    if (this.activeUVGroupIds.size === 0) {
      groupIds.forEach((gid) => this.activeUVGroupIds.add(gid));
    }

    panel.innerHTML = '';
    groupIds.forEach((gid, idx) => {
      const label = (uvGroupNames && uvGroupNames[gid])
        ? uvGroupNames[gid]
        : (idx === 0 ? 'Exterior' : `Group ${idx + 1}`);
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'editor-uv-group-item' + (this.activeUVGroupIds.has(gid) ? ' active' : '');
      item.dataset.group = gid;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', this.activeUVGroupIds.has(gid) ? 'true' : 'false');
      item.title = groups[gid].slice(0, 5).join(', ') + (groups[gid].length > 5 ? '…' : '');
      const check = document.createElement('span');
      check.className = 'editor-uv-group-item-check';
      const text = document.createElement('span');
      text.textContent = label;
      item.appendChild(check);
      item.appendChild(text);
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleUVGroupFilter(gid, model);
        item.classList.toggle('active', this.activeUVGroupIds.has(gid));
        item.setAttribute('aria-selected', this.activeUVGroupIds.has(gid) ? 'true' : 'false');
      });
      panel.appendChild(item);
    });

    // Открытие/закрытие панели по клику на триггер
    const closePanel = () => {
      switcher.classList.remove('open');
      trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', closePanel);
    };
    trigger.onclick = (e) => {
      e.stopPropagation();
      const isOpen = switcher.classList.toggle('open');
      trigger.setAttribute('aria-expanded', String(isOpen));
      if (isOpen) {
        requestAnimationFrame(() => document.addEventListener('click', closePanel));
      } else {
        document.removeEventListener('click', closePanel);
      }
    };
    panel.onclick = (e) => e.stopPropagation();

    // Гарантируем, что фон UV соответствует активным группам
    this.editor.setUVBackground(model, Array.from(this.activeUVGroupIds));
  }

  private toggleUVGroupFilter(groupId: string, model: THREE.Group): void {
    if (!this.editor) return;
    if (this.activeUVGroupIds.has(groupId)) {
      // Не даём снять последнюю активную группу
      if (this.activeUVGroupIds.size <= 1) return;
      this.activeUVGroupIds.delete(groupId);
    } else {
      this.activeUVGroupIds.add(groupId);
    }

    const active = Array.from(this.activeUVGroupIds);
    this.editor.setUVBackground(model, active);

    const switcher = document.getElementById('uv-group-switcher');
    switcher?.querySelectorAll('.editor-uv-group-item').forEach(btn => {
      const b = btn as HTMLElement;
      const gid = b.dataset.group || '';
      b.classList.toggle('active', this.activeUVGroupIds.has(gid));
      b.setAttribute('aria-selected', this.activeUVGroupIds.has(gid) ? 'true' : 'false');
    });

    this.onUVGroupFilterChange?.(active);
  }

  public getActiveUVGroupIds(): string[] {
    return Array.from(this.activeUVGroupIds);
  }

  public close(): void {
    // Проверяем, были ли изменения
    if (this.editor && this.initialCanvasState !== null) {
      const currentState = JSON.stringify(this.editor.saveCanvasData());
      const hasChanges = currentState !== this.initialCanvasState;
      
      if (hasChanges) {
        // Показываем кастомное модальное окно
        this.showConfirmDialog((shouldSave: boolean | null) => {
          if (shouldSave === null) {
            // Пользователь нажал "Отмена" - просто убираем модальное окно, оставляем редактор открытым
            return;
          }
          if (!shouldSave) {
            // Пользователь нажал "Нет" - отменяем изменения и НЕ вызываем onApplyCallback
            if (this.isEditingExisting && this.editor && this.initialCanvasState !== null) {
              // При редактировании существующего - восстанавливаем начальное состояние на канвасе
              const initialData = JSON.parse(this.initialCanvasState);
              this.editor.loadCanvasData(initialData).then(() => {
                // Восстанавливаем оригинальную текстуру на модели
                if (this.onCancelCallback) {
                  this.onCancelCallback();
                }
                // НЕ вызываем onApplyCallback - просто закрываем
                this.doClose();
              });
              return;
            } else {
              // При новом слое - вызываем onCancelCallback для удаления временного принта
              if (this.onCancelCallback) {
                this.onCancelCallback();
              }
              // НЕ вызываем onApplyCallback
              this.doClose();
              return;
            }
          } else {
            // Пользователь нажал "Да" - сохраняем изменения
            if (this.onApplyCallback && this.editor) {
              // ВАЖНО: Сохраняем и dataURL (для текстуры) и canvasData (для структуры объектов)
              const dataUrl = this.editor.exportToDataURL('png', 1.0);
              const canvasData = this.editor.saveCanvasData();
              this.onApplyCallback(dataUrl, canvasData, Array.from(this.activeUVGroupIds));
            }
            this.doClose();
          }
        });
        return;
      }
    }
    
    // Нет изменений - просто закрываем без сохранения
    this.doClose();
  }
  
  private showConfirmDialog(onResult: (shouldSave: boolean | null) => void): void {
    // Создаем overlay в стиле loading-overlay
    const overlay = document.createElement('div');
    overlay.className = 'loading-overlay';
    overlay.id = 'fabrica-confirm-overlay';
    
    // Создаем контент в стиле loading-overlay-content
    const content = document.createElement('div');
    content.className = 'loading-overlay-content';
    
    // Текст сообщения
    const message = document.createElement('div');
    message.className = 'loading-overlay-text';
    message.textContent = t('editor.confirm.save.title');
    
    // Кнопки в стиле loading-overlay-actions
    const buttonsContainer = document.createElement('div');
    buttonsContainer.className = 'loading-overlay-actions';
    
    const yesButton = document.createElement('button');
    yesButton.textContent = t('editor.confirm.save.yes');
    yesButton.className = 'loading-overlay-cancel';
    yesButton.style.background = '#ffffff';
    yesButton.style.color = '#0c0c0c';
    yesButton.onmouseenter = () => {
      yesButton.style.background = 'rgba(255, 255, 255, 0.92)';
    };
    yesButton.onmouseleave = () => {
      yesButton.style.background = '#ffffff';
    };
    
    const noButton = document.createElement('button');
    noButton.textContent = t('editor.confirm.save.no');
    noButton.className = 'loading-overlay-cancel';
    
    const cancelButton = document.createElement('button');
    cancelButton.textContent = t('editor.confirm.save.cancel');
    cancelButton.className = 'loading-overlay-cancel';
    cancelButton.style.background = 'rgba(255, 255, 255, 0.08)';
    cancelButton.style.color = 'rgba(255, 255, 255, 0.85)';
    cancelButton.style.border = '1px solid rgba(255, 255, 255, 0.15)';
    cancelButton.onmouseenter = () => {
      cancelButton.style.background = 'rgba(255, 255, 255, 0.12)';
      cancelButton.style.borderColor = 'rgba(255, 255, 255, 0.2)';
    };
    cancelButton.onmouseleave = () => {
      cancelButton.style.background = 'rgba(255, 255, 255, 0.08)';
      cancelButton.style.borderColor = 'rgba(255, 255, 255, 0.15)';
    };
    
    // Закрытие по Escape - отмена (оставляем редактор открытым)
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close(null);
      }
    };
    
    // Обработчики событий
    const close = (result: boolean | null) => {
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay);
      }
      document.removeEventListener('keydown', handleEscape);
      onResult(result);
    };
    
    yesButton.addEventListener('click', () => close(true));
    noButton.addEventListener('click', () => close(false));
    cancelButton.addEventListener('click', () => close(null));
    
    // Блокируем клики вне модального окна (не закрываем при клике вне)
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        e.stopPropagation();
        // Не закрываем окно при клике вне его
      }
    });
    
    // Предотвращаем всплытие кликов из контента
    content.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    
    document.addEventListener('keydown', handleEscape);
    
    // Добавляем элементы
    buttonsContainer.appendChild(yesButton);
    buttonsContainer.appendChild(noButton);
    buttonsContainer.appendChild(cancelButton);
    content.appendChild(message);
    content.appendChild(buttonsContainer);
    overlay.appendChild(content);
    document.body.appendChild(overlay);
  }
  
  private doClose(): void {
    this.isOpen = false;
    this.hideMaskContextMenu();
    this.modal.style.display = 'none';
    this.onUVGroupFilterChange = undefined;
    // Сбрасываем UV group switcher
    this.activeUVGroupIds.clear();
    const switcher = document.getElementById('uv-group-switcher');
    if (switcher) {
      switcher.classList.add('hidden');
      switcher.classList.remove('open');
      const panel = switcher.querySelector('.editor-uv-group-panel');
      if (panel) panel.innerHTML = '';
    }
    
    // Восстанавливаем aspect-ratio overlay и bottom-toolbar при закрытии редактора
    // Но только если режим cinema/photo активен
    const toolbarUnified = document.querySelector('.toolbar-unified');
    const isCinemaOrPhotoActive = toolbarUnified?.classList.contains('photo-active') || toolbarUnified?.classList.contains('cinema-active');
    
    const aspectRatioOverlay = document.getElementById('aspect-ratio-overlay');
    const bottomToolbar = document.getElementById('bottom-toolbar');
    
    if (isCinemaOrPhotoActive) {
      // Если режим активен - показываем overlay (он сам определит нужен ли он)
      if (aspectRatioOverlay) {
        // Запускаем событие для обновления overlay (он сам решит показывать или нет)
        window.dispatchEvent(new CustomEvent('aspectRatioChange'));
      }
      if (bottomToolbar) {
        bottomToolbar.style.display = '';
      }
    } else {
      // Если режим не активен - просто показываем bottom-toolbar
      if (bottomToolbar) {
        bottomToolbar.style.display = '';
      }
    }
    
    // Возвращаем canvas-container на исходную ширину СРАЗУ
    const canvasContainer = document.getElementById('canvas-container');
    if (canvasContainer) {
      // Сбрасываем inline style
      canvasContainer.style.width = '';
      canvasContainer.style.left = '';
      
      // Восстанавливаем размер рендерера и камеры СРАЗУ
      // Используем правильную ширину с учётом сайдбара (233px)
      const SIDEBAR_WIDTH = 233;
      if (this.sceneManager) {
        const renderer = (this.sceneManager as any).renderer;
        const camera = (this.sceneManager as any).camera;
        if (renderer) {
          const width = window.innerWidth - SIDEBAR_WIDTH;
          const height = window.innerHeight;
          renderer.setSize(width, height);
          if (camera) {
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
          }
        }
      }
    }
    
    // Восстанавливаем исходное положение камеры СРАЗУ
    if (this.originalCameraState && this.sceneManager) {
      const camera = (this.sceneManager as any).camera;
      const controls = (this.sceneManager as any).controls;
      if (camera && controls) {
        camera.position.copy(this.originalCameraState.position);
        controls.target.copy(this.originalCameraState.target);
        controls.update();
      }
    }
    
    // Убираем callback
    if (this.editor) {
      this.editor.setOnChangeCallback(undefined);
      this.editor.setOnRealtimeCallback(undefined);
    }
    
    // Сбрасываем флаги и callbacks
    this.isEditingExisting = false;
    this.currentEditingPrintId = null;
    this.onCancelCallback = undefined;
    this.initialCanvasState = null;
  }

  public getEditor(): CanvasEditor {
    if (!this.editor) {
      throw new Error('Editor not initialized. Call open() or openWithUVBackground() first.');
    }
    return this.editor;
  }
  
  /**
   * Сохраняет текущее состояние канваса как начальное.
   * Вызывается из main.ts после загрузки данных существующего принта.
   */
  public setInitialCanvasState(): void {
    if (this.editor) {
      this.initialCanvasState = JSON.stringify(this.editor.saveCanvasData());
    }
  }
}
